import {createRef, useEffect, useRef, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faCheck,
    faCompress,
    faExpand,
    faPause,
    faPlay,
    faSpinner,
    faTicketAlt, faTimes,
    faVolumeHigh,
    faVolumeXmark
} from "@fortawesome/free-solid-svg-icons";

import './VideoPlayer.scss'
import VideoSeekBar from "./VideoSeekBar";
import {formatSeconds} from "../../lib/static";
import VideoClippingContainer, {VideoClippingConfig} from "./VideoClippingContainer";
import {useApi} from "../../api";

type Props = {
    video: VideoFile
    loop?: boolean
    autoPlay?: boolean
    onEnded?: () => void
    fullScreen: boolean
    enableFullscreen: () => void
}

export default ({ video, fullScreen, enableFullscreen, loop = true, autoPlay = false, onEnded = () => null }: Props) => {
    const api = useApi()

    const containerRef = createRef<HTMLDivElement>()
    const videoRef = createRef<HTMLVideoElement>()

    const [ videoSize, setVideoSize ] = useState<{ width: number, height: number } | null>(null)

    const [ videoLoadingError, setVideoLoadingError ] = useState<boolean>(false)
    const [ totalTime, setTotalTime ] = useState<number | null>(null)
    const [ currentTime, setCurrentTime ] = useState<number | null>(null)
    const [ paused, setPaused ] = useState<boolean>(false)
    const [ muted, setMuted ] = useState<boolean>(false)
    const [ volume, setVolume ] = useState<number>(0)
    const [ ctrlPressed, setCtrlPressed ] = useState<boolean>(false)

    const [ clippingStart, setClippingStart ] = useState<number | null>(null)
    const [ clippingEnd, setClippingEnd ] = useState<number | null>(null)
    const [ clipCreating, setClipCreating ] = useState<boolean>(false)

    const [ clippingConfig, setClippingConfig ] = useState<VideoClippingConfig | null>(null)

    const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2]
    const [ playbackRate, setPlaybackRate ] = useState<number>(1)

    const ctrlRef = useRef<boolean>(false)
    ctrlRef.current = ctrlPressed

    const seekTargetRef = useRef<number>(0)
    const lastPositionSaveRef = useRef<number>(0)

    useEffect(() => {
        if (!videoRef.current) {
            setCurrentTime(null)
            setTotalTime(null)
            return
        }

        setMuted(videoRef.current.muted)
        setVolume(videoRef.current.volume)

        videoRef.current.addEventListener('pause', () => setPaused(true))
        videoRef.current.addEventListener('play', () => setPaused(false))
        videoRef.current.addEventListener('ended', onEnded)
        videoRef.current.addEventListener('loadedmetadata', (e: any) => {
            setCurrentTime(0)
            setTotalTime(e.target.duration)
            if (!videoSize) {
                setVideoSize({
                    width: e.target.videoWidth,
                    height: e.target.videoHeight,
                })
            }
            const target = seekTargetRef.current
            if (target > 5 && target < e.target.duration - 10) {
                e.target.currentTime = target
            }
        })
        videoRef.current.addEventListener('timeupdate', (e: any) => {
            if (!videoRef.current) {
                return
            }
            setCurrentTime(videoRef.current.currentTime)
            const now = Date.now()
            if (now - lastPositionSaveRef.current > 5000 && videoRef.current.currentTime > 10) {
                lastPositionSaveRef.current = now
                api.setWatchPosition(video.hash, videoRef.current.currentTime)
            }
        })

        const onKeyDown = (e: KeyboardEvent) => {
            if (!videoRef.current) {
                return
            }

            if (e.key === 'Control') {
                setCtrlPressed(true)
            }
        }

        const onKeyUp = (e: KeyboardEvent) => {
            if (!videoRef.current) {
                return
            }

            switch (e.key) {
                case 'Control':
                    setCtrlPressed(false)
                    break
                case 'ArrowLeft':
                    if (ctrlRef.current) {
                        break
                    }
                    e.preventDefault()
                    videoRef.current.currentTime -= 10
                    break
                case 'ArrowRight':
                    if (ctrlRef.current) {
                        break
                    }
                    e.preventDefault()
                    videoRef.current.currentTime += 10
                    break
                case ' ':
                    e.preventDefault()
                    videoRef.current.paused ? videoRef.current.play() : videoRef.current.pause()
                    break
            }
        }

        window.addEventListener('keydown', onKeyDown)
        window.addEventListener('keyup', onKeyUp)

        return () => {
            window.removeEventListener('keydown', onKeyDown)
            window.removeEventListener('keyup', onKeyUp)
        }
    }, [videoRef]);

    useEffect(() => {
        setVideoLoadingError(false)
        setCurrentTime(null)
        setTotalTime(null)
        seekTargetRef.current = 0
        lastPositionSaveRef.current = 0
        if (video.fileType === 'video' || video.fileType === 'clip') {
            api.getWatchPosition(video.hash).then(({ position }) => {
                seekTargetRef.current = position || 0
            }).catch(() => {})
        }
    }, [ video ])

    const togglePlay = (e?: any) => {
        if (!videoRef.current) {
            return
        }
        if (videoRef.current.paused) {
            videoRef.current.play()
        } else {
            videoRef.current.pause()
        }
    }

    const toggleMute = (e: any) => {
        if (!videoRef.current) {
            return
        }
        videoRef.current.muted = !videoRef.current.muted
        setMuted(videoRef.current.muted)
    }

    const seekTo = (ts: number) => {
        if (!videoRef.current) {
            return
        }
        videoRef.current.currentTime = ts
    }

    const onVolumeChange = (e: any) => {
        const value = e.target.value / 100
        if (videoRef.current) {
            videoRef.current.volume = value
            setVolume(value)
        }
    }

    const onVideoError = (e: any) => {
        setVideoLoadingError(true)
    }

    useEffect(() => {
        if (clippingEnd !== null) {
            if (!paused) {
                togglePlay()
            }
        } else {

        }
    }, [clippingEnd]);

    useEffect(() => {
        if (clippingStart === null) {
            setClippingEnd(null)
        }
    }, [clippingStart]);

    const classNames = ['video-container']
    if (paused) classNames.push('paused')
    if (muted) classNames.push('muted')
    if (!totalTime) classNames.push('loading')

    const createClip = async () => {
        if (!clippingStart || !clippingEnd || !clippingConfig) {
            return
        }
        const config = {
            start: clippingStart,
            end: clippingEnd,
            clip: clippingConfig,
        }

        setClipCreating(true)
        await api.createClip(video.hash, config)
        setClipCreating(false)

        setClippingStart(null)

        alert('Clip created')
    }

    const clippingDuration = clippingEnd && clippingStart ? clippingEnd - clippingStart : (clippingStart && currentTime ? currentTime - clippingStart : null)

    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.playbackRate = playbackRate
        }
    }, [playbackRate, videoRef])

    const cycleSpeed = () => {
        const idx = SPEEDS.indexOf(playbackRate)
        setPlaybackRate(SPEEDS[(idx + 1) % SPEEDS.length])
    }

    return (
        <div className={classNames.join(' ')} ref={containerRef}>
            <video autoPlay={true}
                   key={video.paths.stream}
                   ref={videoRef}
                   controls={false}
                   onError={onVideoError}
                   onClick={togglePlay}
                   loop={loop}
                   onDoubleClick={enableFullscreen}>
                <source src={video.paths.stream}></source>
            </video>
            {videoLoadingError ? (
                <div className="video-loading-error">
                    <div className="message">
                        Failed to load this video, try to re-index the current directory and start it again.
                    </div>
                </div>
            ) : (
                <>
                    {clippingStart && videoSize ? (
                        <VideoClippingContainer onChange={setClippingConfig}
                                                videoWidth={videoSize.width}
                                                videoHeight={videoSize.height}
                                                />
                    ) : null}
                    <div className="controls">
                        <div className="controls-content">
                            {totalTime && (
                                <VideoSeekBar video={video} currentTime={currentTime} totalTime={totalTime}
                                              seekTo={seekTo}/>
                            )}
                            <div className="control-buttons">
                                {!totalTime ? (
                                    <span><FontAwesomeIcon icon={faSpinner} spin/></span>
                                ) : (
                                    <>
                                        <div className="left">
                                            <button onClick={togglePlay}>
                                                <FontAwesomeIcon icon={paused ? faPlay : faPause}/>
                                            </button>
                                            <span>{formatSeconds(currentTime)} / {formatSeconds(totalTime)}</span>
                                        </div>
                                        <div className="right">
                                            { clippingStart ? (
                                                <>
                                                    <button style={{ width: 'auto' }} onClick={() => seekTo(clippingStart as number)}>
                                                        Clip start: {formatSeconds(clippingStart)}
                                                    </button>
                                                    { clippingEnd ? (
                                                        <button style={{ width: 'auto' }} onClick={() => seekTo(clippingEnd as number)}>
                                                            Clip end: {formatSeconds(clippingEnd)}
                                                        </button>
                                                    ) : null}
                                                    <button style={{ width: 'auto' }}>
                                                        Clip duration: {formatSeconds(clippingDuration)}
                                                    </button>
                                                    { !clipCreating ? (
                                                        <button onClick={() => setClippingEnd(currentTime)} style={{ width: 'auto' }}>
                                                            Set clip end
                                                        </button>
                                                    ) : null }
                                                    { !clipCreating && clippingStart && clippingEnd ? (
                                                        <button style={{ width: 'auto' }} onClick={createClip} title="Create clip">
                                                            <FontAwesomeIcon icon={faCheck}/>
                                                        </button>
                                                    ) : null}
                                                    { !clipCreating ? (
                                                        <button onClick={() => setClippingStart(null)} style={{ width: 'auto' }} title="Cancel">
                                                            <FontAwesomeIcon icon={faTimes}/>
                                                        </button>
                                                    ) : null}
                                                    { clipCreating ? (
                                                        <button style={{ width: 'auto' }}>
                                                            <FontAwesomeIcon icon={faSpinner} spin/>
                                                            Creating clip...
                                                        </button>
                                                    ) : null}

                                                </>
                                            ) : (
                                                <>
                                                    <div className="volume-container">
                                                        <div className="input-wrap">
                                                            <input type="range" disabled={muted} min={0} max={100} step="1"
                                                                   value={volume * 100} onChange={onVolumeChange}/>
                                                        </div>
                                                        <button onClick={toggleMute}>
                                                            <FontAwesomeIcon icon={muted ? faVolumeXmark : faVolumeHigh}/>
                                                        </button>
                                                    </div>
        
                                                    <button onClick={cycleSpeed} title="Playback speed" style={{ width: 'auto' }}>
                                                        {playbackRate}×
                                                    </button>

                                                    <button onClick={() => setClippingStart(currentTime)} title={"Start clipping mode"}>
                                                        <FontAwesomeIcon icon={faTicketAlt}/>
                                                    </button>

                                                    <button onClick={enableFullscreen}>
                                                        <FontAwesomeIcon icon={fullScreen ? faCompress : faExpand}/>
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}